// Trip mode: start a trip and say what you're after, see suggested spots (spots.ts) from your past catches and
// today's weather, record the route while the app is open, and get a summary at the end. Past trips are listed
// under More. Only one trip runs at a time; it survives closing and reopening the app.
import type { Catch, Kind, Trip, Weather } from "../../shared/model.ts";
import { trackKm } from "../../shared/geo.ts";
import { combo } from "../combo.ts";
import { dbAll, dbDel, dbPut } from "../db.ts";
import { $, $maybe, el, icon, toast, twoTap } from "../dom.ts";
import { LOCALE, dFmtY, fmtNum, fmtWhen, speciesLabel, t, tFmt, windFrom } from "../i18n.ts";
import { bigMap } from "../map/bigmap.ts";
import { exportGpx } from "../map/gpx.ts";
import { directionsUrl } from "../map/places.ts";
import { onMapReady } from "../map/ready.ts";
import { getPosition } from "../position.ts";
import { cancelButton, closeSheet, grow, openSheet, primaryButton, sheet } from "../sheet.ts";
import { matchSpecies, sizeLine, speciesName, speciesOptions } from "../species.ts";
import { catches, onRender } from "../state.ts";
import { distanceKm, uid } from "../util.ts";
import { setView } from "../views.ts";
import { fetchWaterTemp, hasWater } from "../water.ts";
import { fetchWeather } from "../weather.ts";
import { groupMessages, groups, me } from "../groups/store.ts";
import { BackgroundGeolocation, LocalNotifications, isNative } from "../native.ts";
import { hasPlus } from "../billing.ts";
import { openPaywall } from "../paywall.ts";
import { suggestSpots, type Conditions, type Reason, type Sample, type Spot } from "./spots.ts";

/** The trip going on now, if any. */
let active: Trip | null = null;
let watchId = 0, nativeWatcher: string | null = null, saveTimer = 0, statsTimer = 0;
let wakeLock: WakeLockSentinel | null = null, screenOn = false;
let layer: L.LayerGroup | null = null, routeLine: L.Polyline | null = null, meDot: L.CircleMarker | null = null;
let spots: Spot[] = [], spotsState: "idle" | "loading" | "ready" | "offline" | "plus" = "idle";

const GOOD_MATCH = 0.7;

/* ---------- what you're after ---------- */
export function targetName(target: Trip["target"]): string {
  if (!target) return t("tripAnything");
  if (target.speciesId) return speciesLabel(target.speciesId);
  return target.species || t(target.kind === "fish" ? "fishPl" : "gamePl");
}
function matchesTarget(target: Trip["target"], kind: Kind, speciesId: string | undefined, name: string): boolean {
  if (!target) return true;
  if (kind !== target.kind) return false;
  if (target.speciesId) return speciesId === target.speciesId;
  if (target.species) return name.trim().toLowerCase() === target.species.trim().toLowerCase();
  return true;
}
/** Catches made during the trip. From the start of the trip's first minute, because the catch form's time has
 *  whole minutes only (a catch logged right after starting would otherwise fall before the start). */
export function catchesDuring(trip: Trip): Catch[] {
  const from = Math.floor(trip.startedAt / 6e4) * 6e4, to = trip.endedAt ?? Date.now();
  return catches.filter((c) => c.time >= from && c.time <= to).sort((a, b) => a.time - b.time);
}
export function fmtDuration(ms: number): string {
  const m = Math.max(0, Math.round(ms / 6e4)), h = Math.floor(m / 60);
  return h ? t("durHm", h, m % 60) : t("durM", m);
}

/* ---------- starting ---------- */
function openStartSheet(): void {
  let kind: Kind | null = "fish";
  const kindChips = el("div", { class: "chips" });
  const speciesInput = el("input", { type: "text", id: "trip-species", autocomplete: "off", placeholder: t("tripSpeciesPh"),
    role: "combobox", "aria-autocomplete": "list", "aria-expanded": "false", "aria-controls": "trip-species-list" });
  const speciesBtn = el("button", { type: "button", class: "combo-btn", tabindex: "-1", "aria-label": t("speciesShowList") }, icon("chevron"));
  const speciesList = el("ul", { class: "combo-list", id: "trip-species-list", role: "listbox", hidden: true });
  const speciesBox = el("div", { class: "combo" }, speciesInput, speciesBtn, speciesList);
  const box = combo({ input: speciesInput, button: speciesBtn, list: speciesList, emptyText: () => t("speciesNoMatch"),
    options: (q) => (kind ? speciesOptions(kind, catches, LOCALE, { recent: t("speciesRecent"), all: t("speciesAll") })(q) : []) });
  sheet.addEventListener("close", box.destroy, { once: true });
  const drawKinds = () => {
    kindChips.replaceChildren(...([["fish", t("fishPl")], ["game", t("gamePl")], [null, t("tripAnything")]] as [Kind | null, string][]).map(([k, label]) =>
      el("button", { class: "chip", type: "button", "aria-pressed": String(kind === k), onclick: () => { kind = k; speciesInput.value = ""; box.close(); drawKinds(); } }, label)));
    speciesBox.hidden = kind == null;
  };
  drawKinds();
  const screen = el("input", { type: "checkbox", id: "trip-screen" });
  openSheet(t("tripStart"), [
    el("div", { class: "f" }, el("span", { class: "lbl", text: t("tripWhat") }), kindChips, speciesBox),
    isNative() ? null : el("label", { class: "toggle" }, screen, el("span", { text: t("tripScreenOn") })),
    el("p", { class: "hint", text: t(isNative() ? "tripRouteNoteNative" : "tripRouteNote") }),
  ], [grow(), cancelButton(), primaryButton(t("tripGo"), () => {
    const name = speciesInput.value.trim();
    const target: Trip["target"] = kind == null ? null : name ? { kind, ...(matchSpecies(name, kind) ? { speciesId: matchSpecies(name, kind)! } : { species: name }) } : { kind };
    closeSheet();
    void startTrip(target, screen.checked);
  })]);
}

async function startTrip(target: Trip["target"], keepScreenOn: boolean): Promise<void> {
  const trip: Trip = { id: uid(), startedAt: Date.now(), endedAt: null, target, track: [], km: 0 };
  await dbPut("trips", trip);
  active = trip;
  setView("map");
  showTripUi();
  void startTracking();
  void setScreenOn(keepScreenOn);
  toast(t("tripStarted"));
  void findSpots();
}

/* ---------- route ---------- */
// In the Android app the native recorder keeps going with the screen off (and shows a notification meanwhile);
// in a browser the route is recorded only while the page is open on screen.
async function startTracking(): Promise<void> {
  if (isNative()) {
    if (nativeWatcher) return;
    await LocalNotifications.requestPermissions().catch(() => {}); // Android 13+: to show the "recording" notification
    nativeWatcher = await BackgroundGeolocation.addWatcher({
      backgroundTitle: t("tripRecordingTitle"), backgroundMessage: t("tripRecordingText"),
      requestPermissions: true, stale: false, distanceFilter: 10,
    }, (loc, err) => {
      if (err) { if (err.code === "NOT_AUTHORIZED") toast(t("tripNoLocation")); return; }
      if (loc) addPoint(loc.latitude, loc.longitude, loc.accuracy);
    });
    return;
  }
  if (!navigator.geolocation || watchId) return;
  watchId = navigator.geolocation.watchPosition((p) => addPoint(p.coords.latitude, p.coords.longitude, p.coords.accuracy),
    (e) => console.warn("Trip location", e.message), { enableHighAccuracy: true, maximumAge: 5000, timeout: 60000 });
}
function stopTracking(): void {
  if (nativeWatcher) void BackgroundGeolocation.removeWatcher({ id: nativeWatcher });
  nativeWatcher = null;
  if (watchId) navigator.geolocation.clearWatch(watchId);
  watchId = 0;
}
function addPoint(lat: number, lon: number, accuracy: number): void {
  const trip = active;
  if (!trip) return;
  const pt: [number, number, number] = [lat, lon, Date.now()];
  meDot?.setLatLng([pt[0], pt[1]]);
  if (accuracy > 60) return; // too rough for the route
  const last = trip.track.at(-1);
  const metres = last ? distanceKm(last[0], last[1], pt[0], pt[1]) * 1000 : Infinity;
  if (metres < 15) return; // standing still: no new point
  trip.track.push(pt);
  if (last) trip.km += metres / 1000;
  routeLine?.addLatLng([pt[0], pt[1]]);
  drawStats();
  // saved after every new point (points come at most every 15 m), so nothing is lost if the phone closes the app
  clearTimeout(saveTimer);
  saveTimer = window.setTimeout(saveTrip, 1000);
}
function saveTrip(): void {
  if (active) void dbPut("trips", active);
}
// the phone may close the app any time it's in the background: save the route the moment it's hidden
document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") saveTrip(); });
addEventListener("pagehide", saveTrip);

// "Keep screen on": the route is only recorded while the screen is on and the app open.
async function setScreenOn(on: boolean): Promise<void> {
  screenOn = on;
  if (!on) { await wakeLock?.release().catch(() => {}); wakeLock = null; return; }
  try { wakeLock = (await navigator.wakeLock?.request("screen")) ?? null; } catch { wakeLock = null; }
}
// the browser drops the screen lock when the app goes to the background; take it again on return
document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible" && active && screenOn) void setScreenOn(true); });

/* ---------- suggested spots ---------- */
/** Your catches and friends' shared catches (with a place) that match what you're after. */
async function samplesFor(target: Trip["target"]): Promise<{ samples: Sample[] }> {
  const samples: Sample[] = [];
  for (const c of catches) {
    if (!c.loc || !matchesTarget(target, c.kind, c.speciesId, speciesName(c))) continue;
    const w = c.weather?.status === "ok" ? c.weather : null;
    samples.push({ lat: c.loc.lat, lon: c.loc.lon, time: c.time, wind: w?.wind ?? null, windDir: w?.windDir ?? null,
      pressure3h: w?.pressure3h ?? null, water: hasWater(c) ? c.water.value : null, weight: 1 });
  }
  for (const g of groups) {
    for (const m of await groupMessages(g.id)) {
      if (m.type !== "catch" || !m.catch.loc || m.from === me.id) continue; // your own shares are already above
      const sc = m.catch, id = matchSpecies(sc.species, sc.kind) ?? undefined;
      if (!matchesTarget(target, sc.kind, id, sc.species)) continue;
      samples.push({ lat: sc.loc!.lat, lon: sc.loc!.lon, time: sc.time, wind: sc.wind ?? null, windDir: sc.windDir ?? null,
        pressure3h: null, water: sc.water ?? null, weight: 0.5, sharedBy: m.name });
    }
  }
  return { samples };
}

async function findSpots(): Promise<void> {
  const trip = active;
  if (!trip) return;
  spotsState = "loading";
  refreshSpotsSheet();
  // where you are: the route's last point, a fresh position, or the map's centre
  const last = trip.track.at(-1);
  const pos = last ? { lat: last[0], lon: last[1] }
    : await getPosition().then((p) => ({ lat: p.lat, lon: p.lon })).catch(() => (bigMap ? { lat: bigMap.getCenter().lat, lon: bigMap.getCenter().lng } : null));
  const now: Conditions = { time: Date.now(), wind: null, windDir: null, pressure3h: null, water: null };
  let gotWeather = false;
  if (pos && navigator.onLine) {
    try {
      const w: Weather = await fetchWeather(pos.lat, pos.lon, now.time);
      Object.assign(now, { wind: w.wind, windDir: w.windDir, pressure3h: w.pressure3h ?? null });
      gotWeather = true;
      if (!trip.startWeather) { trip.startWeather = w; void dbPut("trips", trip); }
      if (trip.target?.kind !== "game") {
        const water = await fetchWaterTemp(pos.lat, pos.lon, now.time).catch(() => null);
        if (water?.value != null && water.src !== "none") now.water = water.value;
      }
    } catch (e) { console.warn("Trip weather", e); }
  }
  if (!hasPlus()) { spots = []; spotsState = "plus"; drawSpots(); refreshSpotsSheet(); return; } // suggested spots are ReelHunt Plus
  const { samples } = await samplesFor(trip.target);
  spots = suggestSpots(samples, now, { from: pos ?? undefined });
  spotsState = gotWeather ? "ready" : "offline";
  drawSpots();
  refreshSpotsSheet();
  // show where you are and the suggestions together
  const pts: L.LatLngTuple[] = [...(pos ? [[pos.lat, pos.lon] as L.LatLngTuple] : []), ...spots.map((s): L.LatLngTuple => [s.lat, s.lon])];
  if (bigMap && pts.length > 1) bigMap.fitBounds(pts, { padding: [70, 70], maxZoom: 14 });
  else if (bigMap && pts.length === 1) bigMap.setView(pts[0], Math.max(bigMap.getZoom(), 13));
}

function spotIcon(i: number, s: Spot): L.DivIcon {
  return L.divIcon({ html: el("div", { class: "spotmark" + (s.match >= GOOD_MATCH ? " good" : ""), text: String(i + 1) }), className: "", iconSize: [34, 34], iconAnchor: [17, 17], popupAnchor: [0, -18] });
}
const reasonsText = (s: Spot) => s.reasons.map((r: Reason) => t(`reason_${r}`)).join(", ");
const spotLines = (s: Spot) => {
  const latest = Math.max(...s.samples.map((x) => x.time));
  const shared = [...new Set(s.samples.map((x) => x.sharedBy).filter((x): x is string => !!x))];
  return [t("spotCatches", s.samples.length, dFmtY.format(latest)), reasonsText(s), s.km != null ? t("spotKm", fmtNum(s.km, 1)) : "",
    shared.length ? t("spotShared", shared.join(", ")) : ""].filter(Boolean);
};

function drawSpots(): void {
  if (!layer) return;
  layer.eachLayer((l) => { if ((l as L.Marker & { isSpot?: boolean }).isSpot) layer!.removeLayer(l); });
  spots.forEach((s, i) => {
    // above the catch markers, which often sit at the same spot
    const mk = L.marker([s.lat, s.lon], { icon: spotIcon(i, s), title: t("spotMatch", Math.round(s.match * 100)), zIndexOffset: 1000 }) as L.Marker & { isSpot?: boolean };
    mk.isSpot = true;
    mk.bindPopup(() => el("div", { class: "pinpop" }, el("b", { text: `${i + 1}. ${t("spotMatch", Math.round(s.match * 100))}` }),
      ...spotLines(s).map((x) => el("small", { text: x, style: "display:block" })),
      el("div", { class: "popbtns" }, el("a", { class: "btn small", href: directionsUrl(s.lat, s.lon), target: "_blank", rel: "noopener" }, t("directionsShort")))));
    mk.addTo(layer!);
  });
}

let spotsSheetOpen = false;
function openSpotsSheet(): void {
  spotsSheetOpen = true;
  sheet.addEventListener("close", () => { spotsSheetOpen = false; }, { once: true });
  refreshSpotsSheet(true);
}
function refreshSpotsSheet(force = false): void {
  if (!force && (!spotsSheetOpen || !sheet.open)) return;
  const trip = active;
  if (!trip) return;
  const name = targetName(trip.target);
  const body: HTMLElement[] = [];
  if (spotsState === "plus") body.push(el("div", { class: "tipcard locked" },
    el("div", { class: "tipshead" }, el("b", { text: t("spotsTitle") }), el("span", { class: "plus-tag" }, icon("star"), t("plusBadge"))),
    el("p", { text: t("spotsPlusText") }),
    el("button", { class: "btn primary small", type: "button", onclick: () => openPaywall("plus", { reason: t("payWhySpots"), onDone: () => void findSpots() }) }, t("plusSee"))));
  else if (spotsState === "loading") body.push(el("p", { class: "hint", text: t("spotsLoading") }));
  else if (!spots.length) body.push(el("p", { text: t("spotsNone", name) }));
  else {
    body.push(el("p", { class: "hint", text: t("spotsFor", name) }));
    if (spotsState === "offline") body.push(el("p", { class: "hint warn", text: t("spotsOffline") }));
    body.push(el("div", {}, ...spots.map((s, i) => el("div", { class: "spotrow" },
      el("div", { class: "spotmark" + (s.match >= GOOD_MATCH ? " good" : ""), text: String(i + 1) }),
      el("div", {}, el("b", { text: t("spotMatch", Math.round(s.match * 100)) }), ...spotLines(s).map((x) => el("small", { text: x })),
        el("div", { class: "popbtns" },
          el("button", { class: "btn small", type: "button", onclick: () => { closeSheet(); bigMap?.setView([s.lat, s.lon], 15); } }, t("spotShow")),
          el("a", { class: "btn small", href: directionsUrl(s.lat, s.lon), target: "_blank", rel: "noopener" }, t("directionsShort"))))))));
  }
  body.push(isNative() ? el("p", { class: "hint", text: t("tripRouteNoteNative") })
    : el("label", { class: "pushrow" }, el("span", { class: "pushtext" }, el("b", { text: t("tripScreenOn") }), el("small", { text: t("tripRouteNote") })),
      el("input", { type: "checkbox", class: "switch", checked: screenOn, onchange: (e) => void setScreenOn(e.currentTarget.checked) })));
  openSheet(t("spotsTitle"), body, [grow(), primaryButton(t("done"), closeSheet)]);
}

/* ---------- on the map ---------- */
function ensureLayer(): void {
  const map = bigMap;
  if (!map || layer) return;
  layer = L.layerGroup().addTo(map);
}
function showTripUi(): void {
  const trip = active;
  if (!trip) return;
  $("#v-map").classList.add("tripping");
  $("#map-trip").setAttribute("aria-pressed", "true");
  $("#tripbar").hidden = false;
  $("#trip-target").textContent = targetName(trip.target);
  drawStats();
  clearInterval(statsTimer);
  statsTimer = window.setInterval(drawStats, 30000);
  ensureLayer();
  if (layer && bigMap) {
    layer.clearLayers();
    routeLine = L.polyline(trip.track.map(([a, b]): L.LatLngTuple => [a, b]), { className: "trip-line", weight: 5, opacity: 0.9 }).addTo(layer);
    const last = trip.track.at(-1);
    meDot = L.circleMarker(last ? [last[0], last[1]] : bigMap.getCenter(), { radius: 8, className: "me-ring", color: "#fff", weight: 3, fillColor: "#2b7de9", fillOpacity: 1 }).addTo(layer);
    if (last) bigMap.setView([last[0], last[1]], Math.max(bigMap.getZoom(), 14));
    drawSpots();
  }
}
function hideTripUi(): void {
  $("#v-map").classList.remove("tripping");
  $("#map-trip").setAttribute("aria-pressed", "false");
  $("#tripbar").hidden = true;
  clearInterval(statsTimer);
  layer?.clearLayers();
  routeLine = null; meDot = null;
}
function drawStats(): void {
  const trip = active;
  const box = $maybe("#trip-stats");
  if (!trip || !box) return;
  box.textContent = [fmtDuration(Date.now() - trip.startedAt), `${fmtNum(trip.km, 1)} km`, t("tripCatches", catchesDuring(trip).length)].join(" · ");
}

$("#map-trip").addEventListener("click", () => {
  if (!active) { openStartSheet(); return; }
  // during a trip the button brings you back to where you are
  const last = active.track.at(-1);
  if (last && bigMap) bigMap.setView([last[0], last[1]], Math.max(bigMap.getZoom(), 14));
});
$("#trip-spots").addEventListener("click", openSpotsSheet);
twoTap($<HTMLButtonElement>("#trip-end"), () => void endTrip(), () => t("tripEnd"), () => t("tripEndConfirm"));

/* ---------- ending and the summary ---------- */
async function endTrip(): Promise<void> {
  const trip = active;
  if (!trip) return;
  trip.endedAt = Date.now();
  trip.km = trackKm(trip.track); // the whole route, measured again
  await dbPut("trips", trip);
  active = null;
  stopTracking();
  await setScreenOn(false);
  hideTripUi();
  spots = []; spotsState = "idle";
  openSummary(trip, false);
  void renderTripsPanel();
}

function fact(label: string, value: string): HTMLElement {
  return el("div", {}, el("dt", { text: label }), el("dd", { text: value }));
}
function openSummary(trip: Trip, fromList: boolean): void {
  const end = trip.endedAt ?? Date.now(), caught = catchesDuring(trip), w = trip.startWeather;
  const weather = w ? [w.temp != null ? Math.round(w.temp) + " °C" : "", w.wind != null ? `${fmtNum(w.wind, 0)} m/s ${w.windDir != null ? windFrom(w.windDir) : ""}` : ""].filter(Boolean).join(" · ") : "";
  const body: (HTMLElement | null)[] = [
    el("dl", { class: "facts tripfacts" },
      fact(t("when"), `${fmtWhen(trip.startedAt)}–${tFmt.format(end)}`),
      fact(t("tripTime"), fmtDuration(end - trip.startedAt)),
      fact(t("tripDistance"), `${fmtNum(trip.km, 1)} km`),
      weather ? fact(t("tripWeatherStart"), weather) : null),
    el("h3", { class: "subhead", text: `${t("tripCaught")} (${caught.length})` }),
    caught.length ? el("ul", {}, ...caught.map((c) => el("li", { text: [speciesName(c), sizeLine(c), tFmt.format(c.time)].filter(Boolean).join(" · ") })))
      : el("p", { class: "hint", text: t("tripNoCatches") }),
  ];
  const buttons: HTMLElement[] = [];
  if (trip.track.length >= 2) {
    buttons.push(el("button", { class: "btn", type: "button", onclick: () => void exportGpx({ pins: [], catches: caught, name: `reelhunt-trip-${new Date(trip.startedAt).toISOString().slice(0, 10)}`,
      routes: [{ id: trip.id, name: `${targetName(trip.target)} ${dFmtY.format(trip.startedAt)}`, points: trip.track.map(([a, b]): [number, number] => [a, b]), createdAt: trip.startedAt }] }) }, t("tripSaveGpx")));
  }
  if (fromList) {
    if (trip.track.length >= 2) buttons.push(el("button", { class: "btn", type: "button", onclick: () => { closeSheet(); showPastRoute(trip); } }, t("tripShowMap")));
    buttons.push(twoTap(el("button", { class: "btn danger", type: "button" }, t("tripDelete")), async () => {
      await dbDel("trips", trip.id); closeSheet(); toast(t("tripDeleted")); void renderTripsPanel();
    }, () => t("tripDelete"), () => t("delConfirm")));
  }
  body.push(el("div", { class: "btns" }, ...buttons));
  openSheet(`${t("tripSummary")} · ${targetName(trip.target)}`, body, [grow(), primaryButton(t("done"), closeSheet)]);
}
function showPastRoute(trip: Trip): void {
  setView("map");
  requestAnimationFrame(() => {
    ensureLayer();
    if (!layer || !bigMap || active) return;
    layer.clearLayers();
    const pts = trip.track.map(([a, b]): L.LatLngTuple => [a, b]);
    L.polyline(pts, { className: "trip-line", weight: 5, opacity: 0.9 }).addTo(layer);
    bigMap.fitBounds(pts, { padding: [40, 40], maxZoom: 16 });
  });
}

/* ---------- past trips under More ---------- */
async function renderTripsPanel(): Promise<void> {
  const box = $maybe("#trips-panel");
  if (!box) return;
  const trips = (await dbAll("trips").catch((): Trip[] => [])).filter((x) => x.endedAt).sort((a, b) => b.startedAt - a.startedAt).slice(0, 10);
  box.replaceChildren(el("h2", { text: t("tripsTitle") }), ...(trips.length ? trips.map((trip) => el("button", { class: "triprow", type: "button", onclick: () => openSummary(trip, true) },
    icon("trip"),
    el("span", {}, el("b", { text: `${dFmtY.format(trip.startedAt)} · ${targetName(trip.target)}` }),
      el("small", { class: "num", text: [fmtDuration(trip.endedAt! - trip.startedAt), `${fmtNum(trip.km, 1)} km`, t("tripCatches", catchesDuring(trip).length)].join(" · ") }))))
    : [el("p", { class: "hint", text: t("tripsNone") })]));
}

onRender(() => { drawStats(); void renderTripsPanel(); });
onMapReady(() => { ensureLayer(); if (active) showTripUi(); });

/** At start: carry on with a trip that was going when the app was closed. */
export async function tripStart(): Promise<void> {
  const open = (await dbAll("trips").catch((): Trip[] => [])).find((x) => !x.endedAt);
  if (!open) return;
  active = open;
  void startTracking();
  if (bigMap) showTripUi();
  void findSpots();
}
